import {
  createSlice,
  createAsyncThunk,
  createSelector } from
'@reduxjs/toolkit';
import { ApolloClient, gql, NormalizedCacheObject } from '@apollo/client';
import { RootState } from '@src/redux/store';
import * as TYPES from '@src/redux/types';

const GRAPHQL = gql`
  query getSystemRoleList(
    $filter: systemRoleFilterInput
    $pagination: paginationInput
    $sort: [sortInput]
  ) {
    getSystemRoleList(
      filter: $filter
      pagination: $pagination
      sort: $sort
    ) {
      items {
        homeUiModuleId
        name
        id
      }
      pagination {
        totalCount
        pageLimit
        pageNumber
      }
      error {
        statusCode
        message
      }
    }
  }
`;

const initialState: TYPES.SystemRoleListState = {
  result: null,
  error: null,
  loading: false
};

export const getSystemRoleListRequest = createAsyncThunk<
  TYPES.SystemRoleList,
  {
    filter?: TYPES.SystemRoleFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  {extra: ApolloClient<NormalizedCacheObject>;}>(

  'system_role/get_system_role_list',
  async (
  params: {
    filter?: TYPES.SystemRoleFilterInput;
    pagination?: TYPES.PaginationInput;
    sort?: TYPES.SortInput[];
  },
  { extra: client }) =>
  {
    try {
      const response = await client.query({
        query: GRAPHQL,
        variables: params
      });
      return response.data.getSystemRoleList;
    } catch (error) {
      const err = error as {message?: String;statusCode?: String;};
      return Promise.reject({
        message: err.message || 'An error occurred',
        statusCode: err.statusCode || '500'
      });
    }
  }
);

const getSystemRoleListSlice = createSlice({
  name: 'get_system_role_list',
  initialState: initialState,
  reducers: {
    getSystemRoleListClear: (state) => {
      state.result = null;
      state.error = null;
      state.loading = false;
    }
  },
  extraReducers: (builder) => {
    builder.
    addCase(getSystemRoleListRequest.pending, (state) => {
      state.result = null;
      state.error = null;
      state.loading = true;
    }).
    addCase(getSystemRoleListRequest.fulfilled, (state, action) => {
      state.result = action.payload;
      state.loading = false;
    }).
    addCase(getSystemRoleListRequest.rejected, (state, action) => {
      state.error = action.payload as Error || {
        message: 'Failed to fetch',
        statusCode: '500'
      };
      state.loading = false;
    });
  }
});

export const { getSystemRoleListClear } =
getSystemRoleListSlice.actions;

export const getSystemRoleList = createSelector(
  (state: RootState) => state.systemRole.getSystemRoleList.result,
  (state: RootState) => state.systemRole.getSystemRoleList.error,
  (state: RootState) => state.systemRole.getSystemRoleList.loading,
  (result, error, loading) => ({
    result,
    error,
    loading
  })
);

export default getSystemRoleListSlice.reducer;